import { Info, X, type LucideIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export type ProfileStatsSummaryItem = {
  Icon?: LucideIcon;
  key: string;
  label: string;
  onPress?: () => void;
  value: number;
};

type ProfileStatsSummaryProps = {
  loading?: boolean;
  primaryStats: readonly ProfileStatsSummaryItem[];
  secondaryInfoItems?: readonly ProfileStatsSummaryInfoItem[];
  secondaryStats?: readonly ProfileStatsSummaryItem[];
};

export type ProfileStatsSummaryInfoItem = {
  description: string;
  label: string;
};

export function ProfileStatsSummary({
  loading = false,
  primaryStats,
  secondaryInfoItems = [],
  secondaryStats = [],
}: ProfileStatsSummaryProps) {
  const [showInfo, setShowInfo] = useState(false);
  const hasSecondaryInfo = secondaryInfoItems.length > 0;

  return (
    <View style={styles.container}>
      <View style={styles.primaryRow}>
        {primaryStats.map((item, index) => (
          <ProfileStatCell
            item={item}
            key={item.key}
            loading={loading}
            showDivider={index < primaryStats.length - 1}
          />
        ))}
      </View>
      {secondaryStats.length > 0 ? (
        <View style={styles.secondaryRow}>
          {secondaryStats.map((item) => (
            <ProfileStatChip item={item} key={item.key} loading={loading} />
          ))}
          {hasSecondaryInfo ? (
            <Pressable
              accessibilityLabel="About profile stats"
              accessibilityRole="button"
              onPress={() => setShowInfo(true)}
              style={({ pressed }) => [
                styles.infoButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Info color={colors.mutedText} size={15} strokeWidth={2.5} />
            </Pressable>
          ) : null}
        </View>
      ) : null}
      {hasSecondaryInfo ? (
        <ProfileStatsInfoModal
          infoItems={secondaryInfoItems}
          onClose={() => setShowInfo(false)}
          visible={showInfo}
        />
      ) : null}
    </View>
  );
}

function ProfileStatCell({
  item,
  loading,
  showDivider,
}: {
  item: ProfileStatsSummaryItem;
  loading: boolean;
  showDivider: boolean;
}) {
  const disabled = loading || !item.onPress;
  const displayValue = loading ? '...' : String(item.value);

  return (
    <Pressable
      accessibilityLabel={`${item.label}: ${displayValue}`}
      accessibilityRole={item.onPress ? 'button' : undefined}
      disabled={disabled}
      onPress={item.onPress}
      style={({ pressed }) => [
        styles.primaryItem,
        showDivider ? styles.primaryItemDivider : null,
        pressed && !disabled ? styles.pressed : null,
      ]}
    >
      <Text style={styles.primaryValue}>{displayValue}</Text>
      <Text numberOfLines={1} style={styles.primaryLabel}>
        {item.label}
      </Text>
    </Pressable>
  );
}

function ProfileStatChip({
  item,
  loading,
}: {
  item: ProfileStatsSummaryItem;
  loading: boolean;
}) {
  const disabled = loading || !item.onPress;
  const displayValue = loading ? '...' : String(item.value);
  const Icon = item.Icon;

  return (
    <Pressable
      accessibilityLabel={`${item.label}: ${displayValue}`}
      accessibilityRole={item.onPress ? 'button' : undefined}
      disabled={disabled}
      onPress={item.onPress}
      style={({ pressed }) => [
        styles.secondaryChip,
        item.onPress ? styles.secondaryChipAction : null,
        pressed && !disabled ? styles.pressed : null,
      ]}
    >
      {Icon ? (
        <Icon color={colors.mutedText} size={14} strokeWidth={2.5} />
      ) : null}
      <Text style={styles.secondaryLabel}>{item.label}</Text>
      <Text style={styles.secondaryValue}>{displayValue}</Text>
    </Pressable>
  );
}

function ProfileStatsInfoModal({
  infoItems,
  onClose,
  visible,
}: {
  infoItems: readonly ProfileStatsSummaryInfoItem[];
  onClose: () => void;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent
      visible={visible}
    >
      <Pressable style={styles.modalBackdrop} onPress={onClose}>
        <Pressable
          style={styles.infoModal}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.infoModalHeader}>
            <Text style={styles.infoModalTitle}>About these stats</Text>
            <Pressable
              accessibilityLabel="Close stats info"
              accessibilityRole="button"
              onPress={onClose}
              style={({ pressed }) => [
                styles.closeButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <X color={colors.mutedText} size={18} strokeWidth={2.5} />
            </Pressable>
          </View>
          <View style={styles.infoList}>
            {infoItems.map((item) => (
              <View key={item.label} style={styles.infoItem}>
                <Text style={styles.infoItemLabel}>{item.label}</Text>
                <Text style={styles.infoItemText}>{item.description}</Text>
              </View>
            ))}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopColor: colors.border,
    borderTopWidth: 1,
    marginTop: spacing.lg,
  },
  primaryRow: {
    flexDirection: 'row',
  },
  primaryItem: {
    alignItems: 'center',
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.md,
  },
  primaryItemDivider: {
    borderRightColor: colors.border,
    borderRightWidth: 1,
  },
  primaryValue: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  primaryLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  pressed: {
    backgroundColor: colors.surfaceTint,
  },
  secondaryRow: {
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  secondaryChip: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 32,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  secondaryChipAction: {
    backgroundColor: colors.accentSoft,
  },
  infoButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  secondaryLabel: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  secondaryValue: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '900',
  },
  modalBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  infoModal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.lg,
    width: '100%',
  },
  infoModalHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  infoModalTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 8,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  infoList: {
    gap: spacing.md,
  },
  infoItem: {
    gap: spacing.xs,
  },
  infoItemLabel: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  infoItemText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
});
